import React from 'react'
import { graphql } from 'gatsby'

// Shared Components
import {
  HeaderBlock,
  CtaButton,
  HerolessQuote,
  OverlapBlock,
  SplitScreen,
  // VerticalOverlap,
  Tricolor,
  InfiniteCards,
} from 'shared-ui'

// Local Features
import Layout from '../features/Layout'
import Seo from '../features/Seo'

import {
  HrTricolor1,
  HrTricolor2,
  HrTricolor3,
  HrTricolor4,
  HrTricolor5,
  HrTricolor6,
  HrTricolor7,
  HrTricolor8,
  HrTricolor9,
  Handshake,
  Tap,
  Binoculars,
} from '../primitives/Icons'

import Left from '../assets/talent-left.svg'

// Image Data
export const query = graphql`
  query TalentPageQuery {
    landing: file(relativePath: { eq: "talentHero.png" }) {
      childImageSharp {
        gatsbyImageData(width: 564, quality: 100, layout: CONSTRAINED)
      }
    }
    g1: file(relativePath: { eq: "talentG1.png" }) {
      childImageSharp {
        gatsbyImageData(width: 667, quality: 100, layout: CONSTRAINED)
      }
    }
    g2: file(relativePath: { eq: "talentG2.png" }) {
      childImageSharp {
        gatsbyImageData(width: 500, quality: 100, layout: CONSTRAINED)
      }
    }
    g3: file(relativePath: { eq: "talentG3.png" }) {
      childImageSharp {
        gatsbyImageData(width: 490, quality: 100, layout: CONSTRAINED)
      }
    }
    g4: file(relativePath: { eq: "talentG4.png" }) {
      childImageSharp {
        gatsbyImageData(width: 501, quality: 100, layout: CONSTRAINED)
      }
    }
    g5: file(relativePath: { eq: "talentG5.png" }) {
      childImageSharp {
        gatsbyImageData(width: 602, quality: 100, layout: CONSTRAINED)
      }
    }
    g6: file(relativePath: { eq: "talentG6.png" }) {
      childImageSharp {
        gatsbyImageData(width: 506, quality: 100, layout: CONSTRAINED)
      }
    }
    g7: file(relativePath: { eq: "talentG7.png" }) {
      childImageSharp {
        gatsbyImageData(width: 479, quality: 100, layout: CONSTRAINED)
      }
    }
    g8: file(relativePath: { eq: "talentG8.png" }) {
      childImageSharp {
        gatsbyImageData(width: 503, quality: 100, layout: CONSTRAINED)
      }
    }
    g9: file(relativePath: { eq: "talentG9.png" }) {
      childImageSharp {
        gatsbyImageData(quality: 100, layout: FULL_WIDTH)
      }
    }
    g10: file(relativePath: { eq: "talentG10.png" }) {
      childImageSharp {
        gatsbyImageData(width: 591, quality: 100, layout: CONSTRAINED)
      }
    }
    avatar: file(relativePath: { eq: "melissaProfile.png" }) {
      childImageSharp {
        gatsbyImageData(width: 81, quality: 100, layout: FIXED)
      }
    }
  }
`

const TalentPage = ({ data }) => {
  const talentPageData = {
    hero: {
      headMax: '545px',
      max: '524px',
      heroText: 'We are the 21st century career center',
      bgColor: '#f9faff',
      pad: '3rem 2rem 0 2rem',
      breakP: '1089px',
      innerMax: '1300px',
      slug: '/contact/',
      subText: (
        <p>
          We reimagined college recruiting for the 21st century, allowing your
          firm to recruit at a national level and increase your ability to
          source diverse candidates.
        </p>
      ),

      graphic: data.landing.childImageSharp.gatsbyImageData,
      vectors: [
        {
          vector: <Left />,
          styles: { top: '0', left: '0', position: 'absolute' },
        },
        {
          vector: null,
          styles: {
            width: '110%',
            maxHeight: '88px',
            position: 'absolute',
            overflow: 'hidden',
            height: '100%',
            bottom: '-30px',
            transform: 'rotate(-1.8deg)',
            backgroundImage:
              'linear-gradient(to right, #19c5ff 0%, #00a1ff 100%)',
          },
        },
      ],
    },
    footer: {
      head: `We arm top organizations with the people and skills they need to thrive and compete`,
      sub: `Ready to change how your company hires the next generation?`,
      buttons: <CtaButton slug="/contact/">Schedule a Demo</CtaButton>,
    },
    tricolor: [
      {
        head: `University \n Fellows`,
        points: [
          {
            icon: <HrTricolor1 />,
            text: 'Make a real impact on a \n firm',
          },
          {
            icon: <HrTricolor2 />,
            text: 'Showcase your skill sets to a potential employer',
          },
          {
            icon: <HrTricolor3 />,
            text: 'Pay off student loans while still in school',
          },
        ],
      },
      {
        head: `Teams and \n Business Leaders`,
        points: [
          {
            icon: <HrTricolor4 />,
            text: 'Access to highly skilled workers in in-demand areas',
          },
          {
            icon: <HrTricolor5 />,
            text:
              'Flexible workforce and variable rate without the premium associated with it',
          },
          {
            icon: <HrTricolor6 />,
            text:
              'Managed program means you do not have to spend time overseeing the work',
          },
        ],
      },
      {
        head: `Companies and \n Human Resources`,
        points: [
          {
            icon: <HrTricolor7 />,
            text: 'Increase access to individuals from diverse backgrounds',
          },
          {
            icon: <HrTricolor8 />,
            text: 'Connect and build deep relationships with emerging talent',
          },
          {
            icon: <HrTricolor9 />,
            text: 'Ensure full time hires are the proper fit',
          },
        ],
      },
    ],
    infiniteCards: [
      {
        id: 'a1',
        icon: <Handshake />,
        head: '03. Engage + Convert',
        sub:
          'Create custom development plans for high potential individuals and demonstrate how much you value their contributions.',
      },
      {
        id: 'b1',
        icon: <Binoculars />,
        head: '01. Discover + Define',
        sub:
          'We work with our HR partners to understand the current and future talent needs of the organization and build a custom plan to fulfill those goals.',
      },
      {
        id: 'c1',
        icon: <Tap />,
        head: '02. Identify + Develop',
        sub:
          "Combining ProMazo’s detailed performance analysis with your team’s  reviews provides HR teams with an entirely  new way to identify and vet talent. You'll never again have to worry whether the person who interviewed will be the same person who shows up on Day 1.",
      },
    ],
  }

  return (
    <Layout pageData={talentPageData}>
      <Seo title="Partnerships with HR" />

      <HeaderBlock
        headerText="A national, digital, scalable, and unbiased way to recruit the next generation of talent"
        pad="10rem 0 8rem"
        mobilePad="4rem 1.8rem 3.6rem"
      />

      <OverlapBlock
        head="Ability is everywhere, but opportunity is not"
        sub="Our mission is to ensure that people from diverse sets of backgrounds have the chance to bring their perspectives to firms. Through our engagements and partnerships with HR and D&I departments at companies, we give Fellows from underrepresented backgrounds an opportunity to showcase their talents and break through to realize their full potential."
        buttons={
          <CtaButton bgColor="#2bb1ff" slug="/contact/">
            Get Started
          </CtaButton>
        }
        imgMargin="-39px 0px -39px -60px"
        graphic={data.g1.childImageSharp.gatsbyImageData}
        maxWidth={667}
        maxHeight={731}
        rightPad="80px"
        textMax="650px"
        accent="#2bb1ff"
        blockW="1400px"
        bgPct="60%"
      />

      <HeaderBlock
        headerText="We partner with HR teams to help build their future workforces"
        pad="10rem 0 8rem"
        mobilePad="4rem 1.8rem 4.6rem"
        headMax={1100}
      />

      <SplitScreen
        first
        head="Inspire & Attract"
        sub="Our Fellows are ambitious individuals who want to make an impact in the professional world. When they are embedded into your teams, you have the opportunity to unleash this creative energy and in turn demonstrate how your organization creates an environment for young professionals to succeed."
        graphic={data.g2.childImageSharp.gatsbyImageData}
        gWidth={500}
        gHeight={750}
        pad="0 23px 9rem"
        textMax="500px"
        innerMax="1100px"
      />
      <SplitScreen
        reverse
        head="Develop & Support"
        sub="Fellows are continuous learners. As they work with your teams they are constantly improving their skillsets and understanding what it takes to be successful at your company."
        graphic={data.g3.childImageSharp.gatsbyImageData}
        gWidth={490}
        gHeight={638}
        pad="9rem 23px"
        innerMax="1150px"
        textMax="490px"
      />
      <SplitScreen
        head="Engage & Recruit"
        sub="Gen Z is focused on more than just a paycheck. They want to make sure a company’s culture aligns with their values. ProMazo allows your managers to build deep relationships with recruits to demonstrate this alignment and through working with their Fellows, identify who is the right fit for your firm."
        graphic={data.g4.childImageSharp.gatsbyImageData}
        gWidth={601}
        gHeight={750}
        innerMiddle="8%"
        pad="9rem 23px 12rem"
        innerMax="1150px"
        textMax="510px"
      />

      <InfiniteCards
        headerText="How it works"
        cards={talentPageData.infiniteCards}
        activeCardHeight={250}
        activeCardWidth={450}
      />

      <HeaderBlock
        headerText="The idea that employees will stay for 20 years is extinct… and that’s Ok!"
        pad="10rem 0 8rem"
        mobilePad="6rem 1.8rem 4.6rem"
      />

      <OverlapBlock
        head={`ProMazo: \n A seamless solution for employee turnover`}
        sub="A two week notice is rarely enough time to transfer all of a departing employee’s thoughts and relationships, let alone find and onboard a replacement. Inevitably ideas and relationships get lost in the transition and your staff are left to carry the load until a replacement is found. With ProMazo’s system; however, turnover is predictable, meaning that new resources can be onboarded prior to departure to ensure nothing is lost and the team does not miss a day."
        buttons={
          <CtaButton bgColor="#2bb1ff" slug="/contact/">
            Get Started
          </CtaButton>
        }
        imgMargin="21px 0px 21px 0"
        graphic={data.g5.childImageSharp.gatsbyImageData}
        maxWidth={567}
        rightPad="28px"
        textMax="570px"
        accent="#2bb1ff"
        bgPct="60%"
      />

      <HeaderBlock
        headerText="The new way to find emerging talent"
        pad="10rem 0 8rem"
        mobilePad="6rem 1.8rem 4.6rem"
      />

      <SplitScreen
        first
        head="The top minds with the latest skillsets"
        sub="Imagine having the Top 5% of software developers, designers, machine learning experts, and AI pioneers embedded within your teams. What could your company unleash with this capability?"
        graphic={data.g6.childImageSharp.gatsbyImageData}
        gWidth={506}
        gHeight={750}
        pad="0 23px 9rem"
        textMax="520px"
        innerMax="1130px"
      />

      <SplitScreen
        reverse
        head="Derisk recruiting and reduce bias"
        sub="Everyone knows the person who showed up at the interview might not be the person who walks in the door on Day 1. Through ProMazo, before a firm has to commit, team leaders can fully vet a prospect and ensure they are the perfect fit for their team. ProMazo’s process also lowers hiring biases because it focuses teams on a person’s work capabilities, not where they went to school or their background."
        graphic={data.g7.childImageSharp.gatsbyImageData}
        gWidth={479}
        gHeight={787}
        pad="9rem 23px"
        innerMax="1170px"
        textMax="520px"
      />
      <SplitScreen
        head="Build a flexible, dynamic Workforce"
        sub="Work is changing. ProMazo’s unique system allows a company to bring on the best of the best and dynamically adjust the bandwidth and skillset of ProMazo Fellows without the premium costs associated with a variable workforce."
        graphic={data.g8.childImageSharp.gatsbyImageData}
        gWidth={503}
        gHeight={790}
        pad="9rem 23px 12rem"
        innerMax="1150px"
        textMax="530px"
      />

      <HeaderBlock
        headerText="What if the next generation could leave college debt free?"
        pad="4rem 0 8rem"
        mobilePad="3rem 1.8rem 3.6rem"
      />

      <OverlapBlock
        head="Ending the Diploma Paradox and solving the $1.7T student debt crisis"
        sub="We are on a mission to end the Diploma Paradox and to build a higher education model that sustainably solves the $1.7 trillion student debt crisis. Our goal is to develop enough opportunities at companies so that any student with the right skills can secure a job, be compensated such that their earnings can pay for their tuition and come out of college debt free."
        buttons={
          <CtaButton bgColor="#2bb1ff" slug="/contact/">
            Get Started
          </CtaButton>
        }
        imgMargin="-25px 0px -25px 0"
        graphic={data.g10.childImageSharp.gatsbyImageData}
        maxWidth={591}
        rightPad="18px"
        textMax="600px"
        accent="#2bb1ff"
        bgPct="60%"
      />

      <HeaderBlock
        pad="14rem 0 3rem"
        headerText="A unique program that is a win-win-win"
        mobilePad="4rem 1.8rem 3.6rem"
      />

      <Tricolor content={talentPageData.tricolor} />

      <HerolessQuote
        avatar={data.avatar.childImageSharp.gatsbyImageData}
        pad="12rem 1.8rem"
        quote={
          "ProMazo's system allowed us to quickly integrate into the company's workflow and teams. As a result, we were able to produce a high impact product that our client still utilizes to this day as part of their everyday operations."
        }
      />
    </Layout>
  )
}

export default TalentPage
